import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  ClipboardList, Plus, Pencil, Trash2, ChevronRight, ChevronDown,
  Users, Calendar, Check, X, Info, CheckCircle2,
  Briefcase, CalendarClock, AlertTriangle, ChefHat
} from "lucide-react";
import { Input } from "../components/ui/input";
import DateInput from "../components/DateInput";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ToggleSwitch as Switch } from "../components/ui/toggle-switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENZE = [
  { value: "giornaliero", label: "Giornaliero" },
  { value: "settimanale", label: "Settimanale" },
  { value: "mensile", label: "Mensile (giorno fisso)" },
  { value: "mensile_relativo", label: "Mensile (es. primo lunedì)" },
];

const TIPI_SCADENZA = [
  { value: "ricorrente", label: "Ricorrente", desc: "Ripete con la frequenza scelta" },
  { value: "data_fissa", label: "Scadenza fissa", desc: "Es. estintori, certificazioni" },
  { value: "finestra", label: "Finestra temporale", desc: "Es. visite mediche (entro la settimana)" },
];

const REPARTI = [
  { value: "tutti", label: "Tutti" },
  { value: "cucina", label: "Cucina" },
  { value: "sala", label: "Sala" },
  { value: "laboratorio", label: "Laboratorio" },
];

const GIORNI_SETTIMANA = [
  { value: 0, label: "Lun" },
  { value: 1, label: "Mar" },
  { value: 2, label: "Mer" },
  { value: 3, label: "Gio" },
  { value: 4, label: "Ven" },
  { value: 5, label: "Sab" },
  { value: 6, label: "Dom" },
];

const SETTIMANE_MESE = [
  { value: 1, label: "Primo" },
  { value: 2, label: "Secondo" },
  { value: 3, label: "Terzo" },
  { value: 4, label: "Quarto" },
  { value: -1, label: "Ultimo" },
];

const TIPI_TASK = [
  { value: "operativa", label: "Operativa", desc: "Richiede completamento (checkbox)", icon: CheckCircle2, color: "#f97316" },
  { value: "informativa", label: "Informativa", desc: "Solo promemoria (no checkbox)", icon: Info, color: "#8b5cf6" },
  { value: "preparazione", label: "Preparazione", desc: "Prepara una ricetta (qtà target)", icon: ChefHat, color: "#14b8a6" },
];

const UNITA_MISURA_PREP = [
  { value: "kg", label: "Kg" },
  { value: "lt", label: "Lt" },
  { value: "pz", label: "Pz" },
];

// Helper per ottenere colore automatico in base al tipo
const getColorByTipo = (tipo) => {
  if (tipo === "informativa") return "#8b5cf6";
  if (tipo === "preparazione") return "#14b8a6";
  return "#f97316";
};

// Helper: un operatore lavora in cucina/laboratorio?
const isOperatoreCucinaOrLab = (o) => {
  const reparto = (o.reparto || "").toLowerCase();
  if (reparto === "cucina" || reparto === "laboratorio") return true;
  const ruolo = (o.ruolo || "").toLowerCase();
  return (
    ruolo.includes("cuoc") ||
    ruolo.includes("cucina") ||
    ruolo.includes("laboratorio") ||
    ruolo.includes("pastic") ||
    ruolo.includes("banconista") ||
    ruolo.includes("panettiere")
  );
};

const TaskGestione = () => {
  const { getAuthHeaders, localeCorrente } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Data
  const [tasks, setTasks] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [ricette, setRicette] = useState([]);
  
  // Dialogs
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  
  // Forms
  const [editTask, setEditTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    nome: "",
    descrizione: "",
    tipo: "operativa",
    frequenza: "giornaliero",
    frequenza_dettaglio: {
      giorni_settimana: [],
      giorno_mese: null,
      settimana_mese: null,
      giorno_settimana_mese: null
    },
    operatori_ids: [],
    // Assegnazione reparto
    assegnazione_tipo: "operatori", // "operatori" o "reparto"
    reparto: null,
    // Nuovi campi scadenza
    tipo_scadenza: "ricorrente",
    data_scadenza: "",
    data_inizio_finestra: "",
    data_fine_finestra: "",
    giorni_anticipo_alert: 7,
    // Orario task (facoltativo)
    orario_inizio: "",
    orario_fine: "",
    // Preparazione
    ricetta_id: "",
    ricetta_nome: "",
    quantita_target: "",
    unita_misura: "kg",
  });
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (localeCorrente?.id) {
      fetchData();
    }
  }, [localeCorrente?.id]);

  const fetchData = async () => {
    if (!localeCorrente?.id) return;
    
    try {
      const [tasksRes, utentiRes, ricetteRes] = await Promise.all([
        axios.get(`${API}/task-gestione?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/utenti`, { headers: getAuthHeaders() }),
        axios.get(`${API}/ricette?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      
      setTasks(tasksRes.data);
      setRicette(ricetteRes.data || []);
      
      // Filtra solo operatori (cucina/sala) del locale corrente
      const ops = utentiRes.data.filter(u => 
        u.ruolo !== "admin" && 
        (u.locali_ids || []).includes(localeCorrente.id)
      );
      setOperatori(ops);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const openTaskDialog = (task = null) => {
    if (task) {
      setEditTask(task);
      setTaskForm({
        nome: task.nome,
        descrizione: task.descrizione || "",
        tipo: task.tipo || "operativa",
        frequenza: task.frequenza,
        frequenza_dettaglio: task.frequenza_dettaglio || {
          giorni_settimana: [],
          giorno_mese: null,
          settimana_mese: null,
          giorno_settimana_mese: null
        },
        operatori_ids: task.operatori_ids || [],
        assegnazione_tipo: task.assegnazione_tipo || "operatori",
        reparto: task.reparto || null,
        tipo_scadenza: task.tipo_scadenza || "ricorrente",
        data_scadenza: task.data_scadenza || "",
        data_inizio_finestra: task.data_inizio_finestra || "",
        data_fine_finestra: task.data_fine_finestra || "",
        giorni_anticipo_alert: task.giorni_anticipo_alert || 7,
        orario_inizio: task.orario_inizio || "",
        orario_fine: task.orario_fine || "",
        ricetta_id: task.ricetta_id || "",
        ricetta_nome: task.ricetta_nome || "",
        quantita_target: task.quantita_target ?? "",
        unita_misura: task.unita_misura || "kg",
      });
    } else {
      setEditTask(null);
      setTaskForm({
        nome: "",
        descrizione: "",
        tipo: "operativa",
        frequenza: "giornaliero",
        frequenza_dettaglio: {
          giorni_settimana: [],
          giorno_mese: null,
          settimana_mese: null,
          giorno_settimana_mese: null
        },
        operatori_ids: [],
        assegnazione_tipo: "operatori",
        reparto: null,
        tipo_scadenza: "ricorrente",
        data_scadenza: "",
        data_inizio_finestra: "",
        data_fine_finestra: "",
        giorni_anticipo_alert: 7,
        orario_inizio: "",
        orario_fine: "",
        ricetta_id: "",
        ricetta_nome: "",
        quantita_target: "",
        unita_misura: "kg",
      });
    }
    setTaskDialogOpen(true);
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    if (!taskForm.nome.trim()) {
      toast.error("Nome obbligatorio");
      return;
    }

    // Validazione in base al tipo di scadenza
    if (taskForm.tipo_scadenza === "ricorrente") {
      // Validazione frequenza solo per ricorrenti
      if (taskForm.frequenza === "settimanale" && taskForm.frequenza_dettaglio.giorni_settimana.length === 0) {
        toast.error("Seleziona almeno un giorno della settimana");
        return;
      }
      if (taskForm.frequenza === "mensile" && !taskForm.frequenza_dettaglio.giorno_mese) {
        toast.error("Inserisci il giorno del mese");
        return;
      }
      if (taskForm.frequenza === "mensile_relativo") {
        if (taskForm.frequenza_dettaglio.settimana_mese === null || taskForm.frequenza_dettaglio.giorno_settimana_mese === null) {
          toast.error("Seleziona settimana e giorno");
          return;
        }
      }
    } else if (taskForm.tipo_scadenza === "data_fissa") {
      if (!taskForm.data_scadenza) {
        toast.error("Inserisci la data di scadenza");
        return;
      }
    } else if (taskForm.tipo_scadenza === "finestra") {
      if (!taskForm.data_inizio_finestra || !taskForm.data_fine_finestra) {
        toast.error("Inserisci le date della finestra temporale");
        return;
      }
    }

    // Validazione preparazione
    if (taskForm.tipo === "preparazione") {
      if (!taskForm.ricetta_id) {
        toast.error("Seleziona la ricetta da preparare");
        return;
      }
      const qt = parseFloat(taskForm.quantita_target);
      if (isNaN(qt) || qt <= 0) {
        toast.error("Inserisci una quantità target valida");
        return;
      }
    }

    // Colore automatico in base al tipo
    const coloreAutomatico = getColorByTipo(taskForm.tipo);

    setSaving(true);
    try {
      const payload = {
        locale_id: localeCorrente.id,
        ...taskForm,
        quantita_target: taskForm.tipo === "preparazione" ? parseFloat(taskForm.quantita_target) : null,
        ricetta_id: taskForm.tipo === "preparazione" ? taskForm.ricetta_id : null,
        ricetta_nome: taskForm.tipo === "preparazione" ? taskForm.ricetta_nome : null,
        unita_misura: taskForm.tipo === "preparazione" ? taskForm.unita_misura : null,
        colore: coloreAutomatico
      };

      if (editTask) {
        const updatePayload = {
          ...taskForm,
          quantita_target: taskForm.tipo === "preparazione" ? parseFloat(taskForm.quantita_target) : null,
          ricetta_id: taskForm.tipo === "preparazione" ? taskForm.ricetta_id : null,
          ricetta_nome: taskForm.tipo === "preparazione" ? taskForm.ricetta_nome : null,
          unita_misura: taskForm.tipo === "preparazione" ? taskForm.unita_misura : null,
          colore: coloreAutomatico,
        };
        await axios.put(`${API}/task-gestione/${editTask.id}`, updatePayload, { headers: getAuthHeaders() });
        toast.success("Task aggiornato");
      } else {
        await axios.post(`${API}/task-gestione`, payload, { headers: getAuthHeaders() });
        toast.success("Task creato");
      }
      
      setTaskDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (task) => {
    setTaskToDelete(task);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;
    
    try {
      await axios.delete(`${API}/task-gestione/${taskToDelete.id}`, { headers: getAuthHeaders() });
      toast.success("Task eliminato");
      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Helpers
  const getFrequenzaLabel = (task) => {
    const freq = task.frequenza;
    const det = task.frequenza_dettaglio || {};
    
    if (freq === "giornaliero") return "Ogni giorno";
    if (freq === "settimanale") {
      const giorni = (det.giorni_settimana || []).map(g => GIORNI_SETTIMANA[g]?.label).join(", ");
      return giorni || "Settimanale";
    }
    if (freq === "mensile") {
      return `Ogni ${det.giorno_mese || "?"} del mese`;
    }
    if (freq === "mensile_relativo") {
      const sett = SETTIMANE_MESE.find(s => s.value === det.settimana_mese)?.label || "?";
      const giorno = GIORNI_SETTIMANA.find(g => g.value === det.giorno_settimana_mese)?.label || "?";
      return `${sett} ${giorno} del mese`;
    }
    return freq;
  };

  const getOperatoreNome = (id) => {
    const op = operatori.find(o => o.id === id);
    return op?.nome || op?.email || op?.telefono || "";
  };

  const toggleGiornoSettimana = (giorno) => {
    const current = taskForm.frequenza_dettaglio.giorni_settimana || [];
    let newGiorni;
    if (current.includes(giorno)) {
      newGiorni = current.filter(g => g !== giorno);
    } else {
      newGiorni = [...current, giorno].sort((a, b) => a - b);
    }
    setTaskForm({
      ...taskForm,
      frequenza_dettaglio: {
        ...taskForm.frequenza_dettaglio,
        giorni_settimana: newGiorni
      }
    });
  };

  const toggleOperatore = (id) => {
    const current = taskForm.operatori_ids || [];
    if (current.includes(id)) {
      setTaskForm({ ...taskForm, operatori_ids: current.filter(o => o !== id) });
    } else {
      setTaskForm({ ...taskForm, operatori_ids: [...current, id] });
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="text-muted-custom">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="task-gestione-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Briefcase className="h-6 w-6 text-orange-400" />
            Bacheca compiti
          </h1>
          <p className="page-subtitle">Configura attività operative e promemoria</p>
        </div>
        <Button 
          onClick={() => openTaskDialog()} 
          className="btn-primary"
          data-testid="new-task-btn"
        >
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Nuovo Task</span>
        </Button>
      </div>

      {/* Tasks List */}
      {tasks.length === 0 ? (
        <div className="empty-state">
          <Briefcase className="empty-state-icon" />
          <p className="empty-state-text">Nessun task di gestione configurato</p>
          <Button 
            onClick={() => openTaskDialog()} 
            variant="link" 
            className="mt-2 text-orange-400"
          >
            Crea il primo task
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => {
            const TipoIcon = TIPI_TASK.find(t => t.value === task.tipo)?.icon || CheckCircle2;
            // Colore automatico basato sul tipo
            const taskColor = getColorByTipo(task.tipo);
            
            return (
              <div 
                key={task.id}
                className="bg-card rounded-2xl border border-border p-4 hover:bg-muted/30 transition-colors"
                data-testid={`task-card-${task.id}`}
              >
                <div className="flex items-start gap-3">
                  {/* Color indicator */}
                  <div 
                    className="w-3 h-12 rounded-full flex-shrink-0"
                    style={{ backgroundColor: taskColor }}
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-white font-medium">{task.nome}</h4>
                      <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                        task.tipo === "operativa" ? "bg-orange-500/20 text-orange-400" :
                        task.tipo === "preparazione" ? "bg-teal-500/20 text-teal-500" :
                        "bg-purple-500/20 text-purple-400"
                      }`}>
                        <TipoIcon className="h-3 w-3" />
                        {task.tipo === "operativa" ? "Operativa" : task.tipo === "preparazione" ? "Preparazione" : "Informativa"}
                      </span>
                      {task.tipo === "preparazione" && task.ricetta_nome && (
                        <span className="text-xs px-2 py-0.5 bg-teal-500/10 rounded-full text-teal-600 dark:text-teal-400">
                          {task.ricetta_nome} · {task.quantita_target} {task.unita_misura}
                        </span>
                      )}
                    </div>
                    
                    {task.descrizione && (
                      <p className="text-xs text-muted-foreground mb-2 line-clamp-1">{task.descrizione}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs px-2 py-0.5 bg-muted/50 rounded-full text-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {getFrequenzaLabel(task)}
                      </span>
                      
                      {/* Assegnazione */}
                      {task.assegnazione_tipo === "reparto" && task.reparto ? (
                        <span className="text-xs px-2 py-0.5 bg-violet-500/20 rounded-full text-violet-600 dark:text-violet-400 flex items-center gap-1">
                          <Briefcase className="h-3 w-3" />
                          {task.reparto === "tutti" ? "Tutti i reparti" : 
                           task.reparto === "cucina" ? "Cucina" :
                           task.reparto === "sala" ? "Sala" :
                           task.reparto === "laboratorio" ? "Laboratorio" : task.reparto}
                        </span>
                      ) : task.operatori_ids?.length > 0 ? (
                        <span className="text-xs px-2 py-0.5 bg-muted/50 rounded-full text-foreground flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {task.operatori_ids.length} operatori
                        </span>
                      ) : null}
                      
                      {/* Orario se presente */}
                      {(task.orario_inizio || task.orario_fine) && (
                        <span className="text-xs px-2 py-0.5 bg-blue-500/20 rounded-full text-blue-600 dark:text-blue-400 flex items-center gap-1">
                          {task.orario_inizio || "--:--"} - {task.orario_fine || "--:--"}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-1 flex-shrink-0">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => openTaskDialog(task)}
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      data-testid={`edit-task-${task.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => confirmDelete(task)}
                      className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      data-testid={`delete-task-${task.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog Task */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-orange-500" />
              {editTask ? "Modifica Task" : "Nuovo Task"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleTaskSubmit} className="space-y-4">
            {/* Nome */}
            <div className="space-y-2">
              <Label className="text-foreground/70">Nome Task *</Label>
              <Input
                value={taskForm.nome}
                onChange={(e) => setTaskForm({ ...taskForm, nome: e.target.value })}
                placeholder="es. Buttare l'immondizia"
                className="bg-card border-border text-foreground"
                data-testid="task-name-input"
              />
            </div>

            {/* Descrizione */}
            <div className="space-y-2">
              <Label className="text-foreground/70">Descrizione (opzionale)</Label>
              <Textarea
                value={taskForm.descrizione}
                onChange={(e) => setTaskForm({ ...taskForm, descrizione: e.target.value })}
                placeholder="Istruzioni o note..."
                className="bg-card border-border text-foreground min-h-[60px]"
              />
            </div>

            {/* Tipo */}
            <div className="space-y-2">
              <Label className="text-foreground/70">Tipo Task *</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {TIPI_TASK.map(tipo => {
                  const Icon = tipo.icon;
                  const isActive = taskForm.tipo === tipo.value;
                  const borderClass = isActive
                    ? tipo.value === "operativa" ? "border-orange-500 bg-orange-500/10"
                      : tipo.value === "preparazione" ? "border-teal-500 bg-teal-500/10"
                      : "border-purple-500 bg-purple-500/10"
                    : "border-border bg-card hover:bg-muted";
                  return (
                    <button
                      key={tipo.value}
                      type="button"
                      onClick={() => setTaskForm({ ...taskForm, tipo: tipo.value })}
                      className={`p-3 rounded-xl border text-left transition-all ${borderClass}`}
                      data-testid={`tipo-task-${tipo.value}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4`} style={isActive ? { color: tipo.color } : undefined} />
                        <span className={`font-medium text-sm ${isActive ? "text-foreground" : "text-foreground/60"}`}>
                          {tipo.label}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{tipo.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Campi Preparazione */}
            {taskForm.tipo === "preparazione" && (
              <div className="space-y-3 p-3 rounded-lg bg-teal-500/5 border border-teal-500/30">
                <div className="flex items-center gap-2 text-teal-600 dark:text-teal-400">
                  <ChefHat className="h-4 w-4" />
                  <span className="text-sm font-medium">Dettagli preparazione</span>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/70">Ricetta *</Label>
                  <Select
                    value={taskForm.ricetta_id}
                    onValueChange={(v) => {
                      const r = ricette.find((rr) => rr.id === v);
                      const ricettaNome = r?.nome || "";
                      // Auto-compila nome se vuoto o se era il default "Preparazione X" precedente
                      const currentNome = (taskForm.nome || "").trim();
                      const wasAutoNome = !currentNome || /^Preparazione\s+/i.test(currentNome);
                      const nuovoNome = wasAutoNome && ricettaNome
                        ? `Preparazione ${ricettaNome}`
                        : taskForm.nome;
                      setTaskForm({
                        ...taskForm,
                        ricetta_id: v,
                        ricetta_nome: ricettaNome,
                        nome: nuovoNome,
                      });
                    }}
                  >
                    <SelectTrigger className="bg-card border-border text-foreground" data-testid="ricetta-select">
                      <SelectValue placeholder={ricette.length === 0 ? "Nessuna ricetta in catalogo" : "Seleziona ricetta"} />
                    </SelectTrigger>
                    <SelectContent>
                      {ricette.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-foreground/70">Quantità *</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={taskForm.quantita_target}
                      onChange={(e) => setTaskForm({ ...taskForm, quantita_target: e.target.value })}
                      placeholder="10"
                      className="bg-card border-border text-foreground"
                      data-testid="quantita-target-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground/70">Unità *</Label>
                    <Select
                      value={taskForm.unita_misura}
                      onValueChange={(v) => setTaskForm({ ...taskForm, unita_misura: v })}
                    >
                      <SelectTrigger className="bg-card border-border text-foreground" data-testid="udm-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UNITA_MISURA_PREP.map((u) => (
                          <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Il task verrà completato automaticamente quando un cuoco crea un lotto di questa ricetta con la quantità prevista.
                </p>
              </div>
            )}

            {/* Tipo Scadenza - NUOVO */}
            <div className="space-y-2">
              <Label className="text-foreground/70">Quando si ripete? *</Label>
              <div className="grid grid-cols-1 gap-2">
                {TIPI_SCADENZA.map(ts => (
                  <button
                    key={ts.value}
                    type="button"
                    onClick={() => setTaskForm({ ...taskForm, tipo_scadenza: ts.value })}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      taskForm.tipo_scadenza === ts.value
                        ? "border-primary bg-primary/10"
                        : "border-border bg-card hover:bg-muted"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <CalendarClock className={`h-4 w-4 ${taskForm.tipo_scadenza === ts.value ? "text-primary" : "text-muted-foreground"}`} />
                      <span className={`font-medium ${taskForm.tipo_scadenza === ts.value ? "text-foreground" : "text-foreground/60"}`}>
                        {ts.label}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{ts.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Campi per SCADENZA FISSA */}
            {taskForm.tipo_scadenza === "data_fissa" && (
              <div className="space-y-3 p-3 rounded-lg bg-muted/30 border border-border">
                <div className="space-y-2">
                  <Label className="text-foreground/70">Data scadenza *</Label>
                  <DateInput value={taskForm.data_scadenza} onChange={(v) => setTaskForm({ ...taskForm, data_scadenza: v })} />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/70">Alert giorni prima</Label>
                  <Input
                    type="number"
                    min="1"
                    max="90"
                    value={taskForm.giorni_anticipo_alert}
                    onChange={(e) => setTaskForm({ ...taskForm, giorni_anticipo_alert: parseInt(e.target.value) || 7 })}
                    className="bg-card border-border text-foreground"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Quando completi questa task, potrai inserire la nuova scadenza.
                </p>
              </div>
            )}

            {/* Campi per FINESTRA TEMPORALE */}
            {taskForm.tipo_scadenza === "finestra" && (
              <div className="space-y-3 p-3 rounded-lg bg-muted/30 border border-border">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-foreground/70">Da *</Label>
                    <DateInput value={taskForm.data_inizio_finestra} onChange={(v) => setTaskForm({ ...taskForm, data_inizio_finestra: v })} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground/70">A *</Label>
                    <DateInput value={taskForm.data_fine_finestra} onChange={(v) => setTaskForm({ ...taskForm, data_fine_finestra: v })} />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  La task può essere completata in qualsiasi momento nella finestra.
                </p>
              </div>
            )}

            {/* Frequenza - solo per RICORRENTE */}
            {taskForm.tipo_scadenza === "ricorrente" && (
              <>
                <div className="space-y-2">
                  <Label className="text-foreground/70">Frequenza *</Label>
                  <Select 
                    value={taskForm.frequenza} 
                    onValueChange={(v) => setTaskForm({ ...taskForm, frequenza: v })}
                  >
                    <SelectTrigger className="bg-card border-border text-foreground">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {FREQUENZE.map(f => (
                        <SelectItem key={f.value} value={f.value} className="text-foreground">{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Giorni settimana (se settimanale) */}
                {taskForm.frequenza === "settimanale" && (
                  <div className="space-y-2">
                    <Label className="text-foreground/70">Giorni della settimana *</Label>
                    <div className="flex flex-wrap gap-2">
                      {GIORNI_SETTIMANA.map(g => (
                        <button
                          key={g.value}
                          type="button"
                          onClick={() => toggleGiornoSettimana(g.value)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            taskForm.frequenza_dettaglio.giorni_settimana?.includes(g.value)
                              ? "bg-orange-500 text-white"
                              : "bg-muted/50 text-foreground hover:bg-muted"
                          }`}
                        >
                          {g.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Giorno mese (se mensile) */}
                {taskForm.frequenza === "mensile" && (
                  <div className="space-y-2">
                    <Label className="text-foreground/70">Giorno del mese *</Label>
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      value={taskForm.frequenza_dettaglio.giorno_mese || ""}
                      onChange={(e) => setTaskForm({
                        ...taskForm,
                        frequenza_dettaglio: {
                          ...taskForm.frequenza_dettaglio,
                          giorno_mese: parseInt(e.target.value) || null
                        }
                      })}
                      placeholder="es. 15"
                      className="bg-card border-border text-foreground"
                    />
                  </div>
                )}

                {/* Mensile relativo (es. primo lunedì) */}
                {taskForm.frequenza === "mensile_relativo" && (
              <div className="space-y-3">
                <Label className="text-foreground/70">Quale giorno del mese? *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Settimana</p>
                    <Select 
                      value={taskForm.frequenza_dettaglio.settimana_mese?.toString() || ""} 
                      onValueChange={(v) => setTaskForm({
                        ...taskForm,
                        frequenza_dettaglio: {
                          ...taskForm.frequenza_dettaglio,
                          settimana_mese: parseInt(v)
                        }
                      })}
                    >
                      <SelectTrigger className="bg-card border-border text-foreground">
                        <SelectValue placeholder="Seleziona..." />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {SETTIMANE_MESE.map(s => (
                          <SelectItem key={s.value} value={s.value.toString()} className="text-foreground">
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Giorno</p>
                    <Select 
                      value={taskForm.frequenza_dettaglio.giorno_settimana_mese?.toString() || ""} 
                      onValueChange={(v) => setTaskForm({
                        ...taskForm,
                        frequenza_dettaglio: {
                          ...taskForm.frequenza_dettaglio,
                          giorno_settimana_mese: parseInt(v)
                        }
                      })}
                    >
                      <SelectTrigger className="bg-card border-border text-foreground">
                        <SelectValue placeholder="Seleziona..." />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {GIORNI_SETTIMANA.map(g => (
                          <SelectItem key={g.value} value={g.value.toString()} className="text-foreground">
                            {g.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {taskForm.frequenza_dettaglio.settimana_mese !== null && taskForm.frequenza_dettaglio.giorno_settimana_mese !== null && (
                  <p className="text-sm text-orange-400 bg-orange-500/10 rounded-lg p-2">
                    Es: {SETTIMANE_MESE.find(s => s.value === taskForm.frequenza_dettaglio.settimana_mese)?.label}{" "}
                    {GIORNI_SETTIMANA.find(g => g.value === taskForm.frequenza_dettaglio.giorno_settimana_mese)?.label} del mese
                  </p>
                )}
              </div>
            )}
              </>
            )}

            {/* Orario Task (facoltativo) */}
            <div className="space-y-3">
              <Label className="text-foreground/70">Orario (facoltativo)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Inizio</Label>
                  <Input
                    type="time"
                    value={taskForm.orario_inizio}
                    onChange={(e) => setTaskForm({ ...taskForm, orario_inizio: e.target.value })}
                    className="bg-card border-border text-foreground"
                    placeholder="--:--"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Fine</Label>
                  <Input
                    type="time"
                    value={taskForm.orario_fine}
                    onChange={(e) => setTaskForm({ ...taskForm, orario_fine: e.target.value })}
                    className="bg-card border-border text-foreground"
                    placeholder="--:--"
                  />
                </div>
              </div>
              {(taskForm.orario_inizio || taskForm.orario_fine) && (
                <p className="text-xs text-muted-foreground">
                  Es: Riunione operativa dalle {taskForm.orario_inizio || "--:--"} alle {taskForm.orario_fine || "--:--"}
                </p>
              )}
            </div>

            {/* Assegnazione: Operatori o Reparto */}
            <div className="space-y-3">
              <Label className="text-foreground/70">Assegna a *</Label>
              
              {/* Toggle Operatori / Reparto */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setTaskForm({ ...taskForm, assegnazione_tipo: "operatori", reparto: null })}
                  className={`p-3 rounded-xl border text-center transition-all ${
                    taskForm.assegnazione_tipo === "operatori"
                      ? "border-primary bg-primary/10"
                      : "border-border bg-card hover:bg-muted"
                  }`}
                >
                  <Users className={`h-5 w-5 mx-auto mb-1 ${taskForm.assegnazione_tipo === "operatori" ? "text-primary" : "text-muted-foreground"}`} />
                  <span className={`text-sm font-medium ${taskForm.assegnazione_tipo === "operatori" ? "text-foreground" : "text-foreground/60"}`}>
                    Operatori
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setTaskForm({ ...taskForm, assegnazione_tipo: "reparto", operatori_ids: [] })}
                  className={`p-3 rounded-xl border text-center transition-all ${
                    taskForm.assegnazione_tipo === "reparto"
                      ? "border-primary bg-primary/10"
                      : "border-border bg-card hover:bg-muted"
                  }`}
                >
                  <Briefcase className={`h-5 w-5 mx-auto mb-1 ${taskForm.assegnazione_tipo === "reparto" ? "text-primary" : "text-muted-foreground"}`} />
                  <span className={`text-sm font-medium ${taskForm.assegnazione_tipo === "reparto" ? "text-foreground" : "text-foreground/60"}`}>
                    Reparto
                  </span>
                </button>
              </div>

              {/* Selezione Operatori */}
              {taskForm.assegnazione_tipo === "operatori" && operatori.length > 0 && (
                <div className="p-3 rounded-lg bg-muted/30 border border-border space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {(taskForm.tipo === "preparazione"
                      ? operatori.filter(isOperatoreCucinaOrLab)
                      : operatori
                    ).map(o => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => toggleOperatore(o.id)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                          taskForm.operatori_ids?.includes(o.id)
                            ? o.ruolo === "cucina" 
                              ? "bg-orange-500 text-white"
                              : "bg-violet-500 text-white"
                            : "bg-muted/50 text-foreground hover:bg-muted"
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${
                          o.ruolo === "cucina" ? "bg-orange-300" : "bg-violet-300"
                        }`} />
                        {o.nome || o.email || o.telefono}
                      </button>
                    ))}
                  </div>
                  {taskForm.tipo === "preparazione" ? (
                    <p className="text-xs text-muted-foreground">
                      Mostrati solo operatori di cucina/laboratorio.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      <span className="inline-block w-2 h-2 rounded-full bg-orange-300 mr-1" /> Cucina
                      <span className="inline-block w-2 h-2 rounded-full bg-violet-300 ml-3 mr-1" /> Sala
                    </p>
                  )}
                </div>
              )}

              {/* Selezione Reparto */}
              {taskForm.assegnazione_tipo === "reparto" && (
                <div className="p-3 rounded-lg bg-muted/30 border border-border">
                  <div className="flex flex-wrap gap-2">
                    {(taskForm.tipo === "preparazione"
                      ? REPARTI.filter(r => r.value === "cucina" || r.value === "laboratorio")
                      : REPARTI
                    ).map(r => (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => setTaskForm({ ...taskForm, reparto: r.value })}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          taskForm.reparto === r.value
                            ? "bg-primary text-white"
                            : "bg-muted/50 text-foreground hover:bg-muted"
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {taskForm.tipo === "preparazione"
                      ? "Solo i reparti che eseguono preparazioni (cucina/laboratorio)."
                      : "Tutti gli operatori del reparto vedranno questa task. Il primo che completa, completa per tutti."}
                  </p>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setTaskDialogOpen(false)} 
                className="flex-1 border-border text-foreground hover:bg-muted"
              >
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={saving} 
                className="flex-1 bg-orange-500 hover:bg-orange-400 text-white font-bold"
                data-testid="save-task-btn"
              >
                {saving ? "Salvataggio..." : (editTask ? "Salva" : "Crea")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm rounded-3xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Elimina Task
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Sei sicuro di voler eliminare <span className="text-foreground font-medium">"{taskToDelete?.nome}"</span>?
            Questa azione eliminerà anche tutte le registrazioni associate.
          </p>
          <DialogFooter className="gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirmOpen(false)} 
              className="flex-1 border-border text-foreground hover:bg-muted"
            >
              Annulla
            </Button>
            <Button 
              onClick={handleDeleteTask} 
              className="flex-1 bg-red-500 hover:bg-red-400 text-white font-bold"
            >
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskGestione;
